import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";
import { AuditTrailForExport } from "../util/getAuditTrailData";

const baseUrl = BASE_URL + "procedure-tmp-rules";

// 📌 Create Procedure Template Rule (POST)
export const createProcedureTemplateRule = createAsyncThunk(
  "procedureTemplateRule/create",
  async (ruleData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${baseUrl}/create`, ruleData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error creating rule");
    }
  }
);

// 📌 Update Procedure Template Rule (PUT)
export const updateProcedureTemplateRule = createAsyncThunk(
  "procedureTemplateRule/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${baseUrl}/update/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating rule");
    }
  }
);

export const updateProcedureTemplateRuleMultiData = createAsyncThunk(
  "procedureTemplateRule/update",
  async ({ data }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${baseUrl}/update-multi-data`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating rule");
    }
  }
);

// 📌 Update Procedure Template Rule Status (PATCH)
export const updateProcedureTemplateRuleStatus = createAsyncThunk(
  "procedureTemplateRule/updateStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`${baseUrl}/update-status`, {
        id,
        status,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error updating rule status"
      );
    }
  }
);

// 📌 Get Procedure Template Rule by ID (GET)
export const getProcedureTemplateRuleById = createAsyncThunk(
  "procedureTemplateRule/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/get-by-id/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching rule");
    }
  }
);

// 📌 Get All Procedure Template Rules (GET)
export const getAllProcedureTemplateRules = createAsyncThunk(
  "procedureTemplateRule/getAll",
  async ({ limit = 5, offset = 0, name = "" }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?limit=${limit}&offset=${offset}&search=${name}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching rules");
    }
  }
);

export const fetchAllProcedureTemplateRules = createAsyncThunk(
  "procedureTemplateRule/fetchAll",
  async ({ searchQuery }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?${searchQuery}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching rules");
    }
  }
);

export const sortProcedureTemplateRules = createAsyncThunk(
  "procedureTemplateRule/sort",
  async ({ limit,offset,sortBy,orderBy }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?limit=${limit}&offset=${offset}&sortBy=${sortBy}&sortOrder=${orderBy}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching rules");
    }
  }
);

// 📌 Delete Procedure Template Rule (DELETE)
export const deleteProcedureTemplateRule = createAsyncThunk(
  "procedureTemplateRule/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${baseUrl}/delete/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error deleting rule");
    }
  }
);

export const exportTempltRulesCSV = createAsyncThunk(
  "procedureTemplateRule/delete",
  async (_, { rejectWithValue }) => {
    try {

     await AuditTrailForExport({
        apiUrl:`${baseUrl}/export-csv`,
        file_name:"procedure-template-rules-data-"+Date.now()+".csv",
        action:"EXPORT_PTR_DATA",
        action_to_show:"Procedure Template Rules Table data exported as CSV"
      })
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error exporting CSV");
    }
  }
);

// 🛠 Procedure Template Rules Slice
const procedureTemplateRulesSlice = createSlice({
  name: "procedureTemplateRule",
  initialState: {
    rules: [],
    rule: null,
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createProcedureTemplateRule.pending, (state) => {
        state.loading = true;
      })
      .addCase(createProcedureTemplateRule.fulfilled, (state, action) => {
        state.loading = false;
        state.rules.push(action.payload);
      })
      .addCase(createProcedureTemplateRule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(updateProcedureTemplateRule.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProcedureTemplateRule.fulfilled, (state, action) => {
        state.loading = false;
        state.rules = state.rules.map((rule) =>
          rule.id === action.payload.id ? action.payload : rule
        );
      })
      .addCase(updateProcedureTemplateRule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(updateProcedureTemplateRuleStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProcedureTemplateRuleStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.rules = state.rules.map((rule) =>
          rule.id === action.payload.id
            ? { ...rule, status: action.payload.status }
            : rule
        );
      })
      .addCase(updateProcedureTemplateRuleStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(getProcedureTemplateRuleById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProcedureTemplateRuleById.fulfilled, (state, action) => {
        state.loading = false;
        state.rule = action.payload.results;
      })
      .addCase(getProcedureTemplateRuleById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      .addCase(getAllProcedureTemplateRules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllProcedureTemplateRules.fulfilled, (state, action) => {
        state.loading = false;
        state.rules = action.payload.results;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getAllProcedureTemplateRules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      .addCase(fetchAllProcedureTemplateRules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllProcedureTemplateRules.fulfilled, (state, action) => {
        state.loading = false;
        state.rules = action.payload.results;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchAllProcedureTemplateRules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      .addCase(sortProcedureTemplateRules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sortProcedureTemplateRules.fulfilled, (state, action) => {
        state.loading = false;
        state.rules = action.payload.results;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(sortProcedureTemplateRules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })


      .addCase(deleteProcedureTemplateRule.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteProcedureTemplateRule.fulfilled, (state, action) => {
        state.loading = false;
        state.rules = state.rules.filter((rule) => rule.id !== action.payload);
      })
      .addCase(deleteProcedureTemplateRule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      });
  },
});

export default procedureTemplateRulesSlice.reducer;
